import {
    Surface,
    Form,
    addEventsToDocument,
    removeEventsFromDocument, Switch,
    TimezonePicker, Select, Label, FormSelect
} from '@vidazoo/ui-kit';
import theme from './theme.module.scss';
import ReportingActions from '../reportingActions/ReportingActions';
import {useCallback, useEffect, useState} from 'react';
import ReportingConstraints from '../reportingConstraints/ReportingConstraints';
import ReportingDBs from '../reportingDBs/ReportingDBs';
import ReportingTimePresetSelector from '../reportingTimePresetSelector/ReportingTimePresetSelector';
import ReportingDimensions from '../reportingDimensions/ReportingDimensions';
import ReportingFilters from '../reportingFilters/ReportingFilters';
import ReportingMetrics from '../reportingMetrics/ReportingMetrics';
import useReportingParamsManager from '../../common/hooks/useReportingParamsManager';
import {observer} from 'mobx-react';
import {DateRangePicker, TimePreset} from '@vidazoo/ui-kit';
import useReportingModel from '../../common/hooks/useReportingModel';
import dayjs from 'dayjs';
import ReportingSubDimensions from "../reportingSubDimensions/ReportingSubDimensions";
import {PERIODS} from "../../common/enums/periods";
import {ReportVerticalType, ReportVerticalTypes} from '../../../../common/enums/ReportVerticalType';

function ReportingHeader({onFullScreen}: {onFullScreen?: () => void}) {
    // Retain the legacy control visually; it no longer changes reporting parameters.
    const [displayVertical, setDisplayVertical] = useState(ReportVerticalType.PLATFORM);
    const {
        pushReportParam, addFilter, addSubDimension, setParam, removeReportParam,
        rollBackParams, rollForwardParams, resetHistory, params, onClearAllParams, onMultiplePaste,
        setFilterKey, setFilterParam, removeFilter, pushFilterValue, removeFilterValue,
        setConstraintParam, removeConstraint, addConstraint, setPreset, setSubGroupParam,
        removeSubGroup, pushSubGroupValue, removeSubGroupValue, pushSubGroupTargetingType,
        removeSubGroupTargetingType, pushSubGroupTargetingOperator, removeSubGroupTargetingOperator,
        pasteSubGroupValues, applyTimePreset, applyComparePeriod
    } = useReportingParamsManager();

    const model = useReportingModel();

    useEffect(() => {
        addEventsToDocument({
            keydown: onDocumentKeyDown,
        });
        applyTimePreset(TimePreset.Now);
        resetHistory();

        return () => {
            removeEventsFromDocument({
                keydown: onDocumentKeyDown,
            });
        };
    }, []);

    const onDocumentKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.defaultPrevented || e.isComposing || e.altKey || !(e.ctrlKey || e.metaKey)) return;
        const target = e.target as HTMLElement;
        if (target?.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"], [role="dialog"]')) return;
        // Physical key codes also work when the active keyboard layout is Hebrew.
        const key = e.code || e.key.toLowerCase();
        const isZ = key === 'KeyZ' || key === 'z';
        const isY = key === 'KeyY' || key === 'y';
        if (!isZ && !(isY && e.ctrlKey && !e.shiftKey)) return;
        e.preventDefault();
        const changed = (isZ && !e.shiftKey) ? rollBackParams() : rollForwardParams();
        if (changed) model.handleSetColumns();
    }, [rollBackParams, rollForwardParams, model]);

    const getReport = () => {
        model.getReport();
    };

    return (
        <Form onSubmit={getReport}>
            <Surface className={theme.surface} level={1}>
                <div className={theme.top}>
                    <ReportingTimePresetSelector/>
                    <div className={theme.database}>
                        <FormSelect className={theme.vertical} items={ReportVerticalTypes}
                            label="Vertical Type" name="verticalType" required searchable fill small maxItems={5}
                            value={displayVertical}
                            onChange={(event) => setDisplayVertical(event.target.value as ReportVerticalType)}/>
                        <div className={theme.dbType}>
                            <ReportingDBs dbType={params.dbType} setParam={setParam}/>
                        </div>
                    </div>
                </div>
                <div className={theme.timeSelectors}>
                    <div className={theme.selectors}>
                        <div className={theme.timeframe}>
                            <DateRangePicker
                                appearance="classic"
                                calendarMode="single"
                                onFromChange={(from) => setParam("from", dayjs(from))}
                                fromDate={dayjs(params.from ?? undefined)}
                                onToChange={(to) => setParam("to", dayjs(to))}
                                toDate={dayjs(params.to ?? undefined)}
                                onPresetChange={setPreset}
                                preset={params.time as TimePreset}
                            />
                            <div className={theme.timezone}>
                                <TimezonePicker
                                    onChange={(e) => setParam("timezone", e.target.value)}
                                    timezone={params.timezone}
                                    selectedLabelFormatter={(label) => label.startsWith('GMT') ? label.split(' - ')[0] : label}
                                    disabled={model.isLoading} fill searchable
                                />
                            </div>
                        </div>
                        {params.isCompare && (
                            <>
                                <Label className={theme.compareTitle} size={13} weight={700} spacing={1.25}>Compare to</Label>
                                <div className={theme.timeframe}>
                                    <DateRangePicker
                                        appearance="classic"
                                        calendarMode="single"
                                        fromDate={dayjs(params.compareFrom ?? undefined)}
                                        toDate={dayjs(params.compareTo ?? undefined)}
                                        onFromChange={(from) => setParam("compareFrom", dayjs(from))}
                                        onToChange={(to) => setParam("compareTo", dayjs(to))}
                                        preset={TimePreset.Custom}
                                        onPresetChange={() => setParam("period", "custom")}
                                    />
                                    <div className={theme.period}>
                                        <Select aria-label="Comparison period" fill value={params.period}
                                            items={Object.entries(PERIODS).map(([label, value]) => ({label, value}))}
                                            onChange={(e) => { setParam("period", e.target.value); applyComparePeriod(); }}/>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    <Switch className={theme.compareSwitch} small label="Compare Dates" checked={!!params.isCompare}
                        onChange={(e) => setParam("isCompare", e.target.checked)}/>
                </div>
                <div className={theme.bottom}>
                    <div className={theme.left}>
                        <ReportingDimensions
                            pushReportParam={pushReportParam}
                            addFilter={addFilter}
                            addSubDimension={addSubDimension}
                            setParam={setParam}
                            removeReportParam={removeReportParam}
                            onMultiplePaste={onMultiplePaste}
                            onClearAllParams={onClearAllParams}
                            groups={params.groups}
                        />
                        <ReportingFilters
                            filters={params.filters}
                            onClearAllParams={onClearAllParams}
                            pasteFilter={(filter) => onMultiplePaste("filters", filter)}
                            setFilterKey={setFilterKey}
                            setFilterParam={setFilterParam}
                            removeFilter={removeFilter}
                            pushFilterValue={pushFilterValue}
                            removeFilterValue={removeFilterValue}
                        />
                        <ReportingSubDimensions
                            subGroups={params.subGroups}
                            onChangeSubGroup={setSubGroupParam}
                            onDeleteSubGroup={removeSubGroup}
                            onAddSubGroupValue={pushSubGroupValue}
                            onRemoveSubGroupValue={removeSubGroupValue}
                            onAddTargetingType={pushSubGroupTargetingType}
                            onDeleteTargetingType={removeSubGroupTargetingType}
                            onAddTargetingOperator={pushSubGroupTargetingOperator}
                            onDeleteTargetingOperator={removeSubGroupTargetingOperator}
                            onClearAllParams={onClearAllParams}
                            onPasteSubGroupValues={pasteSubGroupValues}
                        />
                    </div>
                    <div className={theme.right}>
                        <ReportingMetrics
                            fields={params.fields}
                            setParam={setParam}
                            removeReportParam={removeReportParam}
                            pushReportParam={pushReportParam}
                            addConstraint={addConstraint}
                            onMultiplePaste={onMultiplePaste}
                            onClearAllParams={onClearAllParams}
                        />
                        <ReportingConstraints
                            constraints={params.constraints}
                            onClearAllParams={onClearAllParams}
                            setConstraintParam={setConstraintParam}
                            removeConstraint={removeConstraint}
                        />
                    </div>
                </div>
                <ReportingActions onFullScreen={onFullScreen}/>
            </Surface>
        </Form>
    );
}

export default observer(ReportingHeader);

